import React from 'react'
import { Trans } from '@mc/i18n'
import { PlansTier } from '../../types/planProducts'
import {
  getTranslationResources,
  type Benefits,
  type TransProps,
} from './getTranslationResources'

/**
 * The BenefitTrans component is designed to simplify the translation of tier benefits. It utilizes
 * the @mc/i18n packages to incorporate information into a specified translation key.
 *
 * @component
 * @example
 * // Example usage
 * const ExampleComponent = ({ tier }: { tier: PlansTier }) => {
 *   return (
 *     <BenefitTrans
 *       tier={tier}
 *       name='concurrent_devices'
 *       ns={CHECKOUT_I18N_NAMESPACE}
 *       i18nKey='plans.headers.devicesInfo'
 *     />
 *   );
 * };
 *
 * @param {PlansTier} props.tier - An object representing the tier from which the benefit value is retrieved.
 * @param {string} props.name - The name of the benefit to be translated, which must be one of the
 *  available benefits in the TierBenefit.name enum.
 * @param {string} [props.i18nKey] - The translation key for the benefit. If not provided, a
 *  default key is used based on the benefit name.
 * @param {Object} [props.values] - Additional values for interpolation in the translation.
 * @param {string | number | boolean} [props.defaultValue] - A fallback value to be used if the
 *  benefit value is not found.
 * @param {Object} props - Any valid properties for the Trans component.
 *
 * @description
 * The component behaves differently based on the type of benefit value:
 * - **String**: If the value is a string, it is used as is for translation.
 * - **Number**: If the value is a number, it is included in the translation values under the key
 *  'count,' following the i18next plurals spec.
 * - **Boolean**: If the value is a boolean, the i18nKey is appended with the boolean value.
 *  For example, if `i18nKey='headers.offlineMode'`, corresponding keys like 'headers.offlineMode_true'
 *  and 'headers.offlineMode_false' are expected to exist in the JSON file.
 */
export const BenefitTrans = ({
  tier,
  name,
  i18nKey: _i18nKey,
  values: _values,
  defaultValue,
  ...props
}: {
  tier: PlansTier
  name: Benefits
  i18nKey: TransProps['i18nKey']
  values?: TransProps['values']
  defaultValue?: string | number | boolean
} & TransProps) => {
  const { i18nKey, values } = getTranslationResources({
    name,
    tier,
    defaultValue,
    i18nKey: _i18nKey,
    values: _values,
  })

  if (!i18nKey) return null

  return <Trans i18nKey={i18nKey} values={values} {...props} />
}
